import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DetailTypeCommandCreated,
  DetailTypeCommandUpdated,
  DetailTypeConnectivityUpdated,
  DetailTypeStateUpdated,
  DeviceInfo,
} from "@screencloud/native-players-helpers";
import { merge } from "lodash";

import {
  ScreenDeviceCommandData,
  ScreenDeviceStateData,
  useGetScreenDeviceStateQuery,
} from "../../types.g";
import type { RdmMessage } from "../../hooks/useSharedRdmWebsocket/rdmMessage.types";
import { deviceStateAdapterToDeviceInfo } from "./deviceStateAdapterToDeviceInfo";
import { useRdmMessage } from "./RealtimeRdmProvider";
import { useScreenDeviceRdmContext } from "./ScreenDeviceRdmProvider";

export type DeviceRdmStateValue = {
  deviceState: ScreenDeviceStateData | undefined;
  lastCommand: ScreenDeviceCommandData | null;
  isRdmOnline: boolean;
  buildDeviceInfo: (
    legacyDeviceInfo?: DeviceInfo | null,
  ) => DeviceInfo | undefined;
};

/** Query + WS subscription implementation (use via provider or standalone). */
export function useDeviceRdmStateInternal(
  screenId: string,
  enabled = true,
): DeviceRdmStateValue {
  const skip = !screenId || !enabled;

  const variables = useMemo(() => ({ input: { screenId } }), [screenId]);

  const { data: screenDeviceStateData, refetch: refetchGetScreenDeviceState } =
    useGetScreenDeviceStateQuery({
      // Apollo 3.2.x loop: under cache-and-network every render re-queries, each
      // fetch flips networkStatus (loading->ready) -> re-render -> refetch, forever.
      // nextFetchPolicy pins reads to cache after the first fetch, so re-renders
      // hit cache; refetch() on WS events still goes to network, keeping data fresh.
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      variables,
      skip,
    });

  const deviceState = enabled
    ? (screenDeviceStateData?.getScreenDeviceState?.data ?? undefined)
    : undefined;
  const deviceId = deviceState?.deviceId ?? "";

  const [lastCommand, setLastCommand] =
    useState<ScreenDeviceCommandData | null>(null);

  const latestMessage = useRdmMessage();
  const refetchRef = useRef(refetchGetScreenDeviceState);
  refetchRef.current = refetchGetScreenDeviceState;

  const deviceIdRef = useRef(deviceId);
  deviceIdRef.current = deviceId;

  const lastProcessedMessageRef = useRef<RdmMessage | null>(null);

  const isRdmOnline = Boolean(deviceState?.isOnline);

  const rdmDeviceInfo = useMemo(
    () => (deviceState ? deviceStateAdapterToDeviceInfo(deviceState) : null),
    [deviceState],
  );

  const buildDeviceInfo = useCallback(
    (legacyDeviceInfo?: DeviceInfo | null): DeviceInfo | undefined => {
      if (!isRdmOnline || !rdmDeviceInfo) {
        return legacyDeviceInfo ?? undefined;
      }
      return merge({}, legacyDeviceInfo ?? {}, rdmDeviceInfo) as DeviceInfo;
    },
    [isRdmOnline, rdmDeviceInfo],
  );

  useEffect(() => {
    if (skip) return;
    setLastCommand(null);
    lastProcessedMessageRef.current = null;
  }, [screenId, skip]);

  useEffect(() => {
    if (skip) return;
    if (!latestMessage || !screenId) return;

    const currentDeviceId = deviceIdRef.current;
    if (!currentDeviceId) return;
    if (latestMessage.deviceId !== currentDeviceId) return;
    if (lastProcessedMessageRef.current === latestMessage) return;
    lastProcessedMessageRef.current = latestMessage;

    if (latestMessage["detail-type"] === DetailTypeConnectivityUpdated) {
      // Refetch on any connectivity change so deviceState reflects the new
      // online/offline status.
      void refetchRef.current();
      return;
    }

    if (latestMessage["detail-type"] === DetailTypeStateUpdated) {
      void refetchRef.current();
      return;
    }

    if (
      latestMessage["detail-type"] === DetailTypeCommandCreated ||
      latestMessage["detail-type"] === DetailTypeCommandUpdated
    ) {
      setLastCommand({
        __typename: "ScreenDeviceCommandData",
        commandId: latestMessage.commandId ?? null,
        commandType: latestMessage.commandType ?? null,
        status: latestMessage.status ?? null,
        deviceId: latestMessage.deviceId,
        orgId: latestMessage.orgId,
        spaceId: latestMessage.spaceId,
        payload: latestMessage.payload ?? null,
        result: latestMessage.result ?? null,
        correlationId: null,
        createdBy: null,
        createdAt: null,
        updatedAt: null,
        completedAt: null,
      });

      if (latestMessage.status === "COMPLETED") {
        void refetchRef.current();
      }
    }
  }, [latestMessage, screenId, skip]);

  return {
    deviceState,
    lastCommand,
    isRdmOnline,
    buildDeviceInfo,
  };
}

/**
 * Shared device RDM state. Must be used within a ScreenDeviceRdmProvider whose
 * screenId matches; throws otherwise so a consumer rendered outside the provider
 * (e.g. a modal mounted by the app-root modal manager) fails loudly instead of
 * silently spinning up a duplicate getScreenDeviceState query.
 *
 */
export function useDeviceRdmState(screenId: string): DeviceRdmStateValue {
  const context = useScreenDeviceRdmContext();

  if (!context || context.screenId !== screenId) {
    throw new Error(
      `useDeviceRdmState("${screenId}") must be used within a ScreenDeviceRdmProvider for the same screenId. ` +
        "Wrap the component (e.g. a modal opened by the app-root modal manager) in RealtimeRdmProvider + ScreenDeviceRdmProvider.",
    );
  }

  const { screenId: _screenId, ...state } = context;
  return state;
}
